import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { ArrowLeft, Plus, X } from 'lucide-react';

export const NewProjectPage = () => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [platform, setPlatform] = useState('');
  const [domain, setDomain] = useState('');
  const [modules, setModules] = useState([]);
  const [currentModule, setCurrentModule] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAddModule = () => {
    if (currentModule.trim() && !modules.includes(currentModule.trim())) {
      setModules([...modules, currentModule.trim()]);
      setCurrentModule('');
    }
  };

  const handleRemoveModule = (module) => {
    setModules(modules.filter((m) => m !== module));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post('/projects', {
        name,
        platform,
        domain: domain || null,
        modules,
      });
      toast.success('Project created successfully!');
      navigate(`/projects/${response.data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create project');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 gap-2"
          data-testid="back-button"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Button>

        <div className="mb-8">
          <h1 className="text-3xl font-heading font-bold text-blueprint tracking-tight mb-2">
            Create New Project
          </h1>
          <p className="text-muted-foreground">
            Set up a new project to start generating QA documentation
          </p>
        </div>

        <Card className="p-6 md:p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="name" data-testid="project-name-label">Project Name *</Label>
              <Input
                id="name"
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="E-commerce Mobile App"
                className="mt-1.5"
                data-testid="project-name-input"
              />
            </div>

            <div>
              <Label htmlFor="platform" data-testid="project-platform-label">Platform *</Label>
              <Select value={platform} onValueChange={setPlatform} required>
                <SelectTrigger className="mt-1.5" data-testid="project-platform-select">
                  <SelectValue placeholder="Select platform" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Web">Web</SelectItem>
                  <SelectItem value="Android">Android</SelectItem>
                  <SelectItem value="iOS">iOS</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="domain" data-testid="project-domain-label">Domain (Optional)</Label>
              <Input
                id="domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="E-commerce, Healthcare, Finance, etc."
                className="mt-1.5"
                data-testid="project-domain-input"
              />
            </div>

            <div>
              <Label htmlFor="modules" data-testid="project-modules-label">Modules</Label>
              <div className="flex gap-2 mt-1.5">
                <Input
                  id="modules"
                  type="text"
                  value={currentModule}
                  onChange={(e) => setCurrentModule(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddModule())}
                  placeholder="Login, Dashboard, Payments, etc."
                  data-testid="module-input"
                />
                <Button
                  type="button"
                  onClick={handleAddModule}
                  className="gap-2"
                  data-testid="add-module-button"
                >
                  <Plus className="h-4 w-4" />
                  Add
                </Button>
              </div>
              {modules.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-3">
                  {modules.map((module) => (
                    <div
                      key={module}
                      className="flex items-center gap-1 px-3 py-1 bg-muted rounded-full text-sm"
                      data-testid={`module-tag-${module}`}
                    >
                      <span>{module}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveModule(module)}
                        className="hover:text-destructive transition-colors"
                        data-testid={`remove-module-${module}`}
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="flex gap-3 pt-4">
              <Button
                type="submit"
                disabled={loading || !name || !platform}
                className="flex-1 bg-blueprint hover:bg-blueprint/90 transition-all active:scale-95"
                data-testid="create-project-submit-button"
              >
                {loading ? 'Creating...' : 'Create Project'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/dashboard')}
                data-testid="cancel-button"
              >
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      </div>
    </div>
  );
};